import { Form, Input, Select } from "antd"
import { P2, Button } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useState } from "react"
import { IShiftPlanRes, IShiftSetupRes } from "../interface/shiftData"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { convertTo24Hr, formatSentenceCase, validateTimeInput } from "../utils"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import { TimeInput } from "../components/TimeInput"
import { useGet } from "utils/useFetch"
import { toast } from "react-toastify"

export const ManualEntryModal = ({
  shiftSetup,
  timesheetQueryKey,
  open,
  onClose,
}: {
  shiftSetup: IShiftSetupRes | undefined
  timesheetQueryKey: string
  open: boolean
  onClose: () => void
}) => {
  const [showTimesheetEntry, setShowTimesheetEntry] = useState(false)
  const [selectedShiftPlanId, setSelectedShiftPlanId] = useState<number | null>(
    null
  )
  const [form] = Form.useForm()
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()

  const shiftPlans = (shiftSetup?.data?.shiftPlans || []).filter(
    (plan) => plan.allowManualTimeEntry
  )

  const { data: selectedPlanData } = useGet<IShiftPlanRes>({
    url: selectedShiftPlanId
      ? `/v1/time-attendance/shift-plans/${selectedShiftPlanId}`
      : "",
    instanceKey: "ewaAxiosInstance",
    enabled: !!selectedShiftPlanId,
  })

  const filteredShiftPeriods = selectedPlanData?.data?.shiftPeriods || []

  const { mutate: createEntry, isLoading } = useMutation({
    mutationFn: async (body: {
      shiftPlanId: number
      shiftPeriodId: number
      date: string
      clockin_time: string
      clockout_time: string
      reason: string
      method: string
    }) => {
      return await ewaAxiosInstance.post(
        "/v1/time-attendance/employee/timesheet/manual-entry",
        body
      )
    },
    onSuccess: (data: any) => {
      const res = data?.data
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [timesheetQueryKey],
        })
        res.message && toast.success(res.message)
        form.resetFields()
        onClose()
      }
    },
    onError: onQueryError,
  })

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      createEntry({
        shiftPlanId: values.shiftPlan,
        shiftPeriodId: values.shiftPeriod,
        date: `${values.year}-${values.month}-${values.day}`,
        clockin_time: convertTo24Hr(values.clockIn),
        clockout_time: convertTo24Hr(values.clockOut),
        reason: values.reason ?? "",
        method: "web-app",
      })
    } catch (err) {
      toast.error("Please fill in all required fields.")
    }
  }

  return (
    <CustomModal
      open={open}
      onCancel={() => {
        onClose()
        form.resetFields()
        setShowTimesheetEntry(false)
      }}
      width={444}
      title={
        showTimesheetEntry
          ? "Create manual timesheet entry"
          : "Create manual entry"
      }
      className="[&_.ant-modal-content]:h-fit"
    >
      <Form
        form={form}
        layout="vertical"
        className="[&_.ant-form-item]:mb-2"
      >
        <div className="mb-1">
          <P2 className="font-semibold">Shift plan</P2>
          <Form.Item
            name="shiftPlan"
            rules={[{ required: true, message: "Please select a shift plan" }]}
          >
            <Select
              placeholder="Select the shift plan"
              style={{ width: "100%" }}
              onChange={(value) => {
                setSelectedShiftPlanId(value)
                form.setFieldsValue({ shiftPeriod: undefined })
              }}
            >
              {shiftPlans?.map((shiftPlan) => (
                <Select.Option
                  key={shiftPlan.id}
                  name={shiftPlan.id}
                  value={shiftPlan.id}
                >
                  <span className="font-semibold">
                    {formatSentenceCase(shiftPlan.title)}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div
          className={showTimesheetEntry ? "border-b -mx-4 md:-mx-6 my-2" : ""}
        ></div>

        {showTimesheetEntry && (
          <>
            <Form.Item
              name="shiftPeriod"
              label="Shift period"
              rules={[
                { required: true, message: "Please select a shift period" },
              ]}
            >
              <Select
                placeholder="Select the shift period"
                style={{ width: "100%" }}
              >
                {filteredShiftPeriods.map((shiftPeriod) => (
                  <Select.Option
                    key={shiftPeriod?.id}
                    value={shiftPeriod?.id}
                  >
                    <span className="font-semibold">
                      {formatSentenceCase(shiftPeriod?.title)}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Day">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Form.Item
                    name="day"
                    noStyle
                    rules={[{ required: true, message: "Please enter a day" }]}
                  >
                    <Input
                      placeholder="DD"
                      style={{ width: "100%", textAlign: "center" }}
                      maxLength={2}
                    />
                  </Form.Item>
                </div>
                <span className="text-gray-400">/</span>
                <div className="flex-1">
                  <Form.Item
                    name="month"
                    noStyle
                    rules={[
                      { required: true, message: "Please enter a month" },
                    ]}
                  >
                    <Input
                      placeholder="MM"
                      style={{ width: "100%", textAlign: "center" }}
                      maxLength={2}
                    />
                  </Form.Item>
                </div>
                <span className="text-gray-400">/</span>
                <div className="flex-1">
                  <Form.Item
                    name="year"
                    noStyle
                    rules={[{ required: true, message: "Please enter a year" }]}
                  >
                    <Input
                      placeholder="YYYY"
                      style={{ width: "100%", textAlign: "center" }}
                      maxLength={4}
                    />
                  </Form.Item>
                </div>
              </div>
            </Form.Item>

            <Form.Item
              name="clockIn"
              label="Clock-in"
              rules={[
                { required: true, message: "Please enter clock-in time" },
                {
                  validator: async (_, val) => {
                    const err = validateTimeInput(val)
                    if (err) throw new Error(err)
                  },
                },
              ]}
            >
              <TimeInput />
            </Form.Item>

            <Form.Item
              name="clockOut"
              label="Clock-out"
              rules={[
                { required: true, message: "Please enter clock-out time" },
                {
                  validator: async (_, val) => {
                    const err = validateTimeInput(val)
                    if (err) throw new Error(err)
                  },
                },
              ]}
            >
              <TimeInput />
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason (optional)"
            >
              <Input.TextArea
                placeholder="Enter reasons for the manual entry"
                rows={3}
                maxLength={150}
              />
            </Form.Item>

            <P2 className="text-gray-400 text-xs">
              Note: You can't edit your reason after saving
            </P2>
          </>
        )}

        <div className="mt-1">
          {!showTimesheetEntry && (
            <button
              onClick={() => {
                form
                  .validateFields()
                  .then(() => setShowTimesheetEntry(true))
                  .catch(() => {})
              }}
              className="bg-primary500 text-white px-6 py-2 rounded-md text-sm font-semibold w-full"
            >
              Next
            </button>
          )}
          {showTimesheetEntry && (
            <Button
              disabled={isLoading}
              submitting={isLoading}
              onClick={handleSave}
              className="bg-primary500 text-white px-6 py-2 rounded-md text-sm font-semibold w-full disabled:opacity-50 flex items-center justify-center"
            >
              Save entry
            </Button>
          )}
        </div>
      </Form>
    </CustomModal>
  )
}
